// MaterialLibrary contract (§24.2, §12.3). WP0 created a working version; WP5 owns it.
// toon: MeshToonMaterial on the 4-step ramp, patched with the fresnel rim (map sky-bottom tint),
// a premium gloss term and the optional object-space pattern. Outlines are inverted hulls; ghosts
// are unlit 4×4 ordered-dither holograms (alpha-tested, so they never need sorting).
import {
  BackSide, Color, DataTexture, LinearFilter, Mesh, MeshBasicMaterial, MeshStandardMaterial,
  MeshToonMaterial, NearestFilter, NoColorSpace, RedFormat, RGBAFormat, ShaderMaterial, UniformsLib, UniformsUtils,
  Vector2, type ColorRepresentation, type IUniform, type Material, type Texture,
} from 'three';
import type { QualityTier } from '../Renderer';
import { BAYER_GLSL, GLOSS_GLSL, NOISE_GLSL, PATTERN_GLSL } from './shaders/chunks';

export interface MaterialLibrary {
  toon(o: { color: ColorRepresentation; gloss?: number; rim?: number; pattern?: number; patternColor?: ColorRepresentation; emissive?: ColorRepresentation; emissiveIntensity?: number }): Material;
  outlineMaterial(width: number, color?: ColorRepresentation): Material;
  addOutline(mesh: Mesh, width?: number): Mesh;        // adds an inverted-hull child; returns it
  ghost(color: ColorRepresentation): Material;          // dithered hologram
  glow(color: ColorRepresentation, intensity?: number): Material;   // unlit emissive for bloom
  chrome(): Material;                                   // MeshStandardMaterial metal using scene.environment
  blobShadowTexture: Texture;
  rampTexture: DataTexture;
  quality: QualityTier;
}

/** The library's extra controls, driven by the Renderer (map palette and settings). */
export interface BasicMaterialLibrary extends MaterialLibrary {
  /** Fresnel rim tint: the current map's sky-bottom color (§12.3). */
  setRimColor(c: ColorRepresentation): void;
  /** Outline width multiplier: 1.6 with "High-contrast outlines" (§14.4). */
  setOutlineScale(k: number): void;
  /**
   * Outline pixel floor (INT-B): a 0.025 m outline is never thinner than `minPx` drawing-buffer
   * pixels (wider outlines in proportion) on a view `heightPx` pixels tall; 0 turns it off.
   */
  setOutlinePixels(minPx: number, heightPx: number): void;
  dispose(): void;
}

export type ToonOptions = Parameters<MaterialLibrary['toon']>[0];

/**
 * Uniform objects shared by every material of one library (and by the WP5 part materials built
 * on it), so one call retints every rim or rescales every outline.
 */
export interface LibraryShared {
  rimColor: IUniform<Color>;
  outlineScale: IUniform<number>;
  /** Outline pixel floor: x = min px for a 0.025 m outline (0 = off), y = view height in px. */
  outlinePx: IUniform<Vector2>;
  /** Seconds; animated patterns (galaxy twinkle, sparkles) read it. Rigs and props advance it. */
  time: IUniform<number>;
}

/** §12.2 global ink: outlines and sticker shadows. */
export const INK = '#2A1A3A';
/** §12.3 outline widths in meters. */
export const OUTLINE_WIDTH = { bean: 0.025, prop: 0.035 } as const;
/** §12.3 rim strength and the default gloss. */
export const TOON_DEFAULTS = { rim: 0.35, gloss: 0.5 } as const;

const RAMP_STEPS = [0.35, 0.62, 0.85, 1.0];
const BLOB_SIZE = 64;
const GHOST_ALPHA = 0.55;
const GHOST_SATURATION = 0.4;
const DEFAULT_RIM_COLOR = '#BDB2FF';

const sharedByLibrary = new WeakMap<MaterialLibrary, LibraryShared>();

/** The shared uniforms of `lib` (a private set for libraries this module did not create). */
export function libraryShared(lib: MaterialLibrary): LibraryShared {
  let s = sharedByLibrary.get(lib);
  if (!s) {
    s = createShared();
    sharedByLibrary.set(lib, s);
  }
  return s;
}

function createShared(): LibraryShared {
  const shared = { rimColor: { value: new Color(DEFAULT_RIM_COLOR) }, outlineScale: { value: 1 }, outlinePx: { value: new Vector2(0, 1) }, time: { value: 0 } };
  pxByScale.set(shared.outlineScale, shared.outlinePx);
  return shared;
}

const pxByScale = new WeakMap<IUniform<number>, IUniform<Vector2>>();

/**
 * The outline pixel-floor uniform that goes with a library's shared outline scale (map-kit hulls
 * reach the library only through that uniform); an inert one for an unknown scale.
 */
export function outlinePxFor(scale: IUniform<number>): IUniform<Vector2> {
  return pxByScale.get(scale) ?? { value: new Vector2(0, 1) };
}

/**
 * GLSL: the hull extrusion width (m) at view depth `d` — §12.3's width × (0.6 + 0.4 × d/25), but
 * never under the pixel floor (at gameplay distance the §12.3 widths are only 1–2 px, which loses
 * the chunky toon ink). `projectionMatrix[2][3]` tells a perspective camera from an orthographic one.
 */
export const OUTLINE_WIDTH_GLSL = /* glsl */ `
uniform vec2 uOutlinePx;
float so_outlineWidth(float width, float d) {
  float w = width * (0.6 + 0.4 * d / 25.0);
  if (uOutlinePx.x <= 0.0) return w;
  float depth = projectionMatrix[2][3] < -0.5 ? d : 1.0;
  float mpp = 2.0 * depth / (projectionMatrix[1][1] * uOutlinePx.y);
  return max(w, uOutlinePx.x * (width / 0.025) * mpp);
}`;

export function createRampTexture(): DataTexture {
  const tex = new DataTexture(Uint8Array.from(RAMP_STEPS, (v) => Math.round(v * 255)), RAMP_STEPS.length, 1, RedFormat);
  tex.minFilter = NearestFilter;
  tex.magFilter = NearestFilter;
  tex.generateMipmaps = false;
  tex.colorSpace = NoColorSpace;
  tex.needsUpdate = true;
  return tex;
}

/** Radial falloff in the alpha channel; the color is the global ink. */
function createBlobTexture(): DataTexture {
  const data = new Uint8Array(BLOB_SIZE * BLOB_SIZE * 4);
  const ink = new Color(INK);
  const half = (BLOB_SIZE - 1) / 2;
  for (let y = 0; y < BLOB_SIZE; y++) {
    for (let x = 0; x < BLOB_SIZE; x++) {
      const r = Math.min(1, Math.hypot(x - half, y - half) / half);
      const i = (y * BLOB_SIZE + x) * 4;
      data[i] = Math.round(ink.r * 255);
      data[i + 1] = Math.round(ink.g * 255);
      data[i + 2] = Math.round(ink.b * 255);
      data[i + 3] = Math.round(Math.pow(1 - r * r, 1.5) * 255);
    }
  }
  const tex = new DataTexture(data, BLOB_SIZE, BLOB_SIZE, RGBAFormat);
  tex.minFilter = LinearFilter;
  tex.magFilter = LinearFilter;
  tex.generateMipmaps = false;
  tex.needsUpdate = true;
  return tex;
}

/**
 * Single-bone skinning. WP5 part geometry binds every vertex to exactly one bone (weight 1), so the
 * stock chunks' four bone fetches per vertex are wasted work: these replacements fetch one. They
 * are only used by WP5 part materials and the library hull (no other skinned meshes exist).
 */
export const SKIN1 = {
  base: /* glsl */ `
#ifdef USE_SKINNING
  mat4 boneMatX = getBoneMatrix( skinIndex.x );
#endif`,
  normal: /* glsl */ `
#ifdef USE_SKINNING
  mat4 skinMatrix = bindMatrixInverse * boneMatX * bindMatrix;
  objectNormal = vec4( skinMatrix * vec4( objectNormal, 0.0 ) ).xyz;
#endif`,
  vertex: /* glsl */ `
#ifdef USE_SKINNING
  transformed = ( bindMatrixInverse * ( boneMatX * ( bindMatrix * vec4( transformed, 1.0 ) ) ) ).xyz;
#endif`,
} as const;

/** Swaps the stock skinning chunks of a built-in shader for the single-bone versions. */
export function singleBoneSkinning(vertexShader: string): string {
  return vertexShader
    .replace('#include <skinbase_vertex>', SKIN1.base)
    .replace('#include <skinnormal_vertex>', SKIN1.normal)
    .replace('#include <skinning_vertex>', SKIN1.vertex);
}

/**
 * §12.3 inverted hull, skinning-aware: extrudes along the normal by width × (0.6 + 0.4 ×
 * viewDistance/25). Part meshes (WP5 rigs and props) carry a per-vertex hull weight (`aInfo.y`)
 * and smoothed hull normals (`aOutlineNormal`); plain meshes get the defaults (weight 1, normal).
 */
export const HULL_VERTEX = /* glsl */ `
uniform float uWidth;
uniform float uScale;
attribute vec4 aInfo;
attribute vec3 aOutlineNormal;
#include <common>
#include <skinning_pars_vertex>
#include <fog_pars_vertex>
${OUTLINE_WIDTH_GLSL}
void main() {
  vec3 objectNormal = dot(aOutlineNormal, aOutlineNormal) > 0.25 ? aOutlineNormal : normal;
  ${SKIN1.base}
  ${SKIN1.normal}
  vec3 transformed = position;
  ${SKIN1.vertex}
  vec4 mvPosition = modelViewMatrix * vec4(transformed, 1.0);
  vec3 n = normalize(normalMatrix * objectNormal + vec3(0.0, 0.0, 1e-6));
  mvPosition.xyz += n * so_outlineWidth(uWidth * uScale, -mvPosition.z) * aInfo.y;
  gl_Position = projectionMatrix * mvPosition;
  if (aInfo.y < 0.001) gl_Position = vec4(2.0, 2.0, 2.0, 1.0);
  #include <fog_vertex>
}`;

export const HULL_FRAGMENT = /* glsl */ `
uniform vec3 uColor;
#include <common>
#include <fog_pars_fragment>
void main() {
  gl_FragColor = vec4(uColor, 1.0);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
  #include <fog_fragment>
}`;

/** Defaults for the part attributes on plain meshes: (mode 0, hull weight 1, casts shadow, 0). */
const HULL_DEFAULT_ATTRIBUTES = { aInfo: [0, 1, 1, 0], aOutlineNormal: [0, 0, 0] } as const;

const GHOST_VERTEX = /* glsl */ `
varying vec3 vNormalV;
varying vec3 vViewDir;
#include <common>
#include <skinning_pars_vertex>
void main() {
  vec3 objectNormal = normal;
  #include <skinbase_vertex>
  #include <skinnormal_vertex>
  vec3 transformed = position;
  #include <skinning_vertex>
  vec4 mvPosition = modelViewMatrix * vec4(transformed, 1.0);
  vNormalV = normalize(normalMatrix * objectNormal);
  vViewDir = normalize(-mvPosition.xyz);
  gl_Position = projectionMatrix * mvPosition;
}`;

// 4×4 ordered dither: alpha-tested, so ghosts never need sorting (§12.4).
const GHOST_FRAGMENT = /* glsl */ `
uniform vec3 uColor;
uniform float uAlpha;
varying vec3 vNormalV;
varying vec3 vViewDir;
${BAYER_GLSL}
void main() {
  float rim = pow(1.0 - abs(dot(normalize(vNormalV), normalize(vViewDir))), 2.0);
  if (so_bayer4(gl_FragCoord.xy) >= min(1.0, uAlpha + rim * 0.35)) discard;
  gl_FragColor = vec4(uColor + rim * 0.6, 1.0);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
}`;

/** Patches a MeshToonMaterial with the rim, gloss and pattern terms. */
function patchToon(m: MeshToonMaterial, shared: LibraryShared, o: { rim: number; gloss: number; pattern: number; patternColor: Color }): void {
  const uniforms = {
    uRimColor: shared.rimColor,
    uTime: shared.time,
    uRim: { value: o.rim },
    uGloss: { value: o.gloss },
    uPattern: { value: o.pattern },
    uPatternColor: { value: o.patternColor },
  };
  m.onBeforeCompile = (shader) => {
    Object.assign(shader.uniforms, uniforms);
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', '#include <common>\nvarying vec3 vSoObj;')
      .replace('#include <begin_vertex>', '#include <begin_vertex>\nvSoObj = position;');
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>
varying vec3 vSoObj;
uniform vec3 uRimColor; uniform float uRim; uniform float uGloss; uniform float uPattern; uniform vec3 uPatternColor; uniform float uTime;
${NOISE_GLSL}
${PATTERN_GLSL}`)
      .replace('#include <color_fragment>', `#include <color_fragment>
if (uPattern > 0.5) diffuseColor.rgb = so_pattern(vSoObj, diffuseColor.rgb, uPatternColor, uPattern, uTime);`)
      .replace('#include <lights_pars_begin>', `#include <lights_pars_begin>\n${GLOSS_GLSL}`)
      .replace('#include <opaque_fragment>', `outgoingLight += so_glossRim(normal, geometryViewDir, uGloss, uRim, uRimColor);
#include <opaque_fragment>`);
  };
  m.customProgramCacheKey = () => 'so-toon-v2';
}

function colorKey(c: ColorRepresentation | undefined): string {
  return c === undefined ? '-' : new Color(c).getHexString();
}

/** The WP5 library: toon ramp + rim + gloss + patterns, inverted-hull outlines, dithered ghosts. */
export function createMaterialLibrary(quality: QualityTier): BasicMaterialLibrary {
  const cache = new Map<string, Material>();
  const shared = createShared();
  const rampTexture = createRampTexture();
  const blobShadowTexture = createBlobTexture();

  function cached<M extends Material>(key: string, make: () => M): M {
    const hit = cache.get(key);
    if (hit) return hit as M;
    const m = make();
    cache.set(key, m);
    return m;
  }

  function toon(o: ToonOptions): Material {
    const rim = o.rim ?? TOON_DEFAULTS.rim;
    const gloss = o.gloss ?? TOON_DEFAULTS.gloss;
    const pattern = o.pattern ?? 0;
    const key = `toon:${colorKey(o.color)}:${colorKey(o.emissive)}:${o.emissiveIntensity ?? 1}:${rim}:${gloss}:${pattern}:${colorKey(o.patternColor)}`;
    return cached(key, () => {
      const m = new MeshToonMaterial({ color: o.color, gradientMap: rampTexture });
      if (o.emissive !== undefined) {
        m.emissive.set(o.emissive);
        m.emissiveIntensity = o.emissiveIntensity ?? 1;
      }
      patchToon(m, shared, { rim, gloss, pattern, patternColor: new Color(o.patternColor ?? o.color) });
      return m;
    });
  }

  function outlineMaterial(width: number, color: ColorRepresentation = INK): Material {
    return cached(`outline:${width}:${colorKey(color)}`, () => {
      const m = new ShaderMaterial({
        // `uScale` is shared by reference (not merged, which would clone it) so one setting updates every outline.
        uniforms: { ...UniformsUtils.clone(UniformsLib.fog), uWidth: { value: width }, uColor: { value: new Color(color) }, uScale: shared.outlineScale, uOutlinePx: shared.outlinePx },
        vertexShader: HULL_VERTEX,
        fragmentShader: HULL_FRAGMENT,
        side: BackSide,
        fog: true,
      });
      m.defaultAttributeValues = { ...m.defaultAttributeValues, ...HULL_DEFAULT_ATTRIBUTES };
      return m;
    });
  }

  function addOutline(mesh: Mesh, width: number = OUTLINE_WIDTH.prop): Mesh {
    const hull = new Mesh(mesh.geometry, outlineMaterial(width));
    hull.name = 'outline';
    hull.castShadow = false;
    hull.receiveShadow = false;
    mesh.add(hull);
    return hull;
  }

  function ghost(color: ColorRepresentation): Material {
    return cached(`ghost:${colorKey(color)}`, () => {
      const c = new Color(color);
      const lum = c.r * 0.2126 + c.g * 0.7152 + c.b * 0.0722;
      c.lerp(new Color(lum, lum, lum), 1 - GHOST_SATURATION);
      return new ShaderMaterial({
        uniforms: { uColor: { value: c }, uAlpha: { value: GHOST_ALPHA } },
        vertexShader: GHOST_VERTEX,
        fragmentShader: GHOST_FRAGMENT,
      });
    });
  }

  function glow(color: ColorRepresentation, intensity = 1): Material {
    return cached(`glow:${colorKey(color)}:${intensity}`, () => new MeshBasicMaterial({
      color: new Color(color).multiplyScalar(intensity),
      toneMapped: false,
    }));
  }

  function chrome(): Material {
    return cached('chrome', () => new MeshStandardMaterial({ color: '#E8EEF4', metalness: 1, roughness: 0.08 }));
  }

  const lib: BasicMaterialLibrary = {
    toon, outlineMaterial, addOutline, ghost, glow, chrome,
    blobShadowTexture, rampTexture, quality,
    setRimColor(c) {
      shared.rimColor.value.set(c);
    },
    setOutlineScale(k) {
      shared.outlineScale.value = k;
    },
    setOutlinePixels(minPx, heightPx) {
      shared.outlinePx.value.set(Math.max(0, minPx), Math.max(1, heightPx));
    },
    dispose() {
      for (const m of cache.values()) m.dispose();
      cache.clear();
      rampTexture.dispose();
      blobShadowTexture.dispose();
    },
  };
  sharedByLibrary.set(lib, shared);
  return lib;
}
